import { useEffect, useRef, useState } from 'react'
import store from '../../../store'
import { snapshotTickEquals } from '../../../util/snapshotTick'
import { Snapshot } from '../../../reducer/security.reducer'
import {
    ComputeSlTargetLiveParams,
    computeSlTargetLiveState,
    slTargetLiveStateEquals,
    SlTargetLiveState,
} from './computeSlTargetLiveState'

const pickSnapshots = (keys: string[]): Record<string, Snapshot> => {
    const all = store.getState().securityStore.snapshots
    const result: Record<string, Snapshot> = {}
    for (const key of keys) {
        if (all[key]) result[key] = all[key]
    }
    return result
}

const snapshotsTickEqualForKeys = (keys: string[], prev: Record<string, Snapshot>, next: Record<string, Snapshot>) => {
    for (const key of keys) {
        if (!snapshotTickEquals(prev[key], next[key])) return false
    }
    return true
}

export function useSlTargetLiveValidation(
    params: ComputeSlTargetLiveParams,
    snapshotKeys: string[]
): SlTargetLiveState {
    const paramsRef = useRef(params)
    paramsRef.current = params

    const keysKey = snapshotKeys.join('\0')
    const keysRef = useRef(snapshotKeys)
    keysRef.current = snapshotKeys

    const snapshotsRef = useRef<Record<string, Snapshot>>({})

    const [live, setLive] = useState<SlTargetLiveState>(() =>
        computeSlTargetLiveState({
            ...paramsRef.current,
            snapshots: pickSnapshots(keysRef.current),
        })
    )

    useEffect(() => {
        const snapshots = pickSnapshots(keysRef.current)
        snapshotsRef.current = snapshots
        const next = computeSlTargetLiveState({ ...paramsRef.current, snapshots })
        setLive((prev) => (slTargetLiveStateEquals(prev, next) ? prev : next))
    }, [
        keysKey,
        params.addStopLoss,
        params.addTarget,
        params.slValue,
        params.targetValue,
        params.slValueType,
        params.targetValueType,
        params.isSingleSecurity,
        params.isLongPosition,
        params.singleSecurityQuantity,
        params.positionKey,
        params.mode,
        params.showSLTargetInfo,
        params.effectivePositionAvgPrice,
        params.effectivePosition,
        params.tradeOrders,
        params.incomingPositions,
        params.selectedSecurity?.priceMultiplier,
    ])

    useEffect(() => {
        if (!keysRef.current.length) return

        return store.subscribe(() => {
            const nextSnapshots = pickSnapshots(keysRef.current)
            if (snapshotsTickEqualForKeys(keysRef.current, snapshotsRef.current, nextSnapshots)) {
                return
            }
            snapshotsRef.current = nextSnapshots
            const next = computeSlTargetLiveState({
                ...paramsRef.current,
                snapshots: nextSnapshots,
            })
            setLive((prev) => (slTargetLiveStateEquals(prev, next) ? prev : next))
        })
    }, [keysKey])

    return live
}
